/**
 * Cliente del backoffice del News Service (Fase 4b).
 *
 * Endpoints backend (`apps/backend/src/routes/admin/news.ts`):
 *   - GET   /admin/news/sources                  → { sources }
 *   - PATCH /admin/news/sources/:id              → { source }
 *   - GET   /admin/news/articles?…filtros…       → { items, page, pageSize } + meta.total
 *   - GET   /admin/news/articles/:id             → { article }
 *   - POST  /admin/news/articles/:id/hide        → { id, status }
 *   - POST  /admin/news/articles/:id/reclassify  → 202 { articleId, jobId }
 *
 * Envelope `{ success, data, meta? }` + auth por cookie admin (igual que el
 * resto del cliente admin). Reusa los helpers de `_core.js`.
 */
import { get, patchJsonAdmin, postJsonAdmin } from './_core.js';

export const NEWS_CEFR_VALUES = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type NewsCefr = (typeof NEWS_CEFR_VALUES)[number];

export const NEWS_SENSITIVITY_VALUES = ['safe', 'mild', 'sensitive', 'graphic'] as const;
export type NewsSensitivity = (typeof NEWS_SENSITIVITY_VALUES)[number];

export const NEWS_STATUS_VALUES = ['active', 'hidden', 'error'] as const;
export type NewsStatus = (typeof NEWS_STATUS_VALUES)[number];

/** Las 17 IPTC top-level Media Topics (espejo de `packages/news-ingest/src/taxonomy.ts`). */
export const NEWS_IPTC_CATEGORIES = [
  'arts, culture, entertainment and media',
  'conflict, war and peace',
  'crime, law and justice',
  'disaster, accident and emergency incident',
  'economy, business and finance',
  'education',
  'environment',
  'health',
  'human interest',
  'labour',
  'lifestyle and leisure',
  'politics',
  'religion',
  'science and technology',
  'society',
  'sport',
  'weather',
] as const;

export interface NewsSource {
  id: string;
  slug: string;
  name: string;
  publisher: string | null;
  feedUrl: string;
  homepageUrl: string | null;
  lang: string;
  categoryHint: string | null;
  cefrHint: string | null;
  enabled: boolean;
  fetchCadenceMinutes: number;
  lastFetchedAt: string | null;
  lastEtag: string | null;
  lastModified: string | null;
  lastStatus: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NewsArticleListItem {
  id: string;
  sourceId: string;
  sourceSlug: string;
  sourceName: string;
  title: string;
  url: string;
  excerpt: string | null;
  imageUrl: string | null;
  iptcCategory: string | null;
  iptcSubcategory: string | null;
  cefr: string | null;
  sensitivity: string;
  status: string;
  publishedAt: string | null;
  fetchedAt: string;
  classifiedAt: string | null;
}

export interface NewsArticleDetail extends NewsArticleListItem {
  author: string | null;
  lang: string;
  tags: string[];
  ourSummary: string | null;
  discussionPrompts: string[];
  createdAt: string;
  updatedAt: string;
}

export interface NewsArticleFilters {
  cefr?: string;
  category?: string;
  sensitivity?: string;
  source?: string;
  status?: string;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface NewsArticleListResponse {
  items: NewsArticleListItem[];
  page: number;
  pageSize: number;
  total: number | undefined;
}

export async function fetchNewsSources(): Promise<{ sources: NewsSource[] }> {
  const res = await get<{ sources: NewsSource[] }>('/admin/news/sources');
  return res.data;
}

export interface NewsSourcePatch {
  enabled?: boolean;
  fetchCadenceMinutes?: number;
}

export async function patchNewsSource(
  id: string,
  patch: NewsSourcePatch,
): Promise<{ source: NewsSource }> {
  // El backend valida que venga al menos una clave y rechaza otras; replicamos
  // solo las dos columnas operativas editables.
  const body: Record<string, unknown> = {};
  if (patch.enabled !== undefined) body.enabled = patch.enabled;
  if (patch.fetchCadenceMinutes !== undefined) {
    body.fetchCadenceMinutes = patch.fetchCadenceMinutes;
  }
  return patchJsonAdmin<{ source: NewsSource }>(
    `/admin/news/sources/${encodeURIComponent(id)}`,
    body,
  );
}

export async function fetchNewsArticles(
  filters: NewsArticleFilters = {},
): Promise<NewsArticleListResponse> {
  const params = new URLSearchParams();
  if (filters.cefr) params.set('cefr', filters.cefr);
  if (filters.category) params.set('category', filters.category);
  if (filters.sensitivity) params.set('sensitivity', filters.sensitivity);
  if (filters.source) params.set('source', filters.source);
  if (filters.status) params.set('status', filters.status);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.q) params.set('q', filters.q);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const qs = params.toString();
  const res = await get<{ items: NewsArticleListItem[]; page: number; pageSize: number }>(
    `/admin/news/articles${qs ? `?${qs}` : ''}`,
  );
  return {
    items: res.data.items,
    page: res.data.page,
    pageSize: res.data.pageSize,
    total: res.meta?.total,
  };
}

export async function fetchNewsArticle(id: string): Promise<{ article: NewsArticleDetail }> {
  const res = await get<{ article: NewsArticleDetail }>(
    `/admin/news/articles/${encodeURIComponent(id)}`,
  );
  return res.data;
}

export async function hideNewsArticle(id: string): Promise<{ id: string; status: string }> {
  return postJsonAdmin<{ id: string; status: string }>(
    `/admin/news/articles/${encodeURIComponent(id)}/hide`,
    {},
  );
}

export async function reclassifyNewsArticle(
  id: string,
): Promise<{ articleId: string; jobId: string }> {
  return postJsonAdmin<{ articleId: string; jobId: string }>(
    `/admin/news/articles/${encodeURIComponent(id)}/reclassify`,
    {},
  );
}
